import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { InputProps } from "antd/lib/input"
import React, { ForwardedRef, LegacyRef, useState } from "react"
import InputMask from "react-input-mask"
import { omit } from "ramda"
import { Input as AntInput } from "antd"
import { classNames } from "../../../utils/classNames"

export type BaseInputProps = Omit<InputProps, "autoComplete" | "size"> & {
    formatValue?: (rawValue: Maybe<string>) => Maybe<string>
    parseValue?: (formattedValue: Maybe<string>) => Maybe<string> // todo: make 'value' and parseValue return value generic
    mask?: string
    hideAbility?: boolean
    autoComplete?: string
    size?: "small" | "default" | "large" | "x-large"
}

/** Input component. Based on Ant Input: https://3x.ant.design/components/input/) - visit the link for props documentation. */
export const Input = React.forwardRef<AntInput, BaseInputProps>(
    (
        {
            size = "default",
            autoComplete = "off",
            formatValue,
            parseValue,
            mask,
            hideAbility,
            value: propValue,
            readOnly,
            disabled,
            className: propClassName,
            onFocus,
            ...restProps
        }: BaseInputProps,
        ref: ForwardedRef<AntInput>
    ) => {
        const [isRevealed, setIsRevealed] = useState(!hideAbility)
        const inputProps: InputProps = restProps

        const renderHideBtn = () => (
            <button type="button" className={classes.hideBtn} onClick={() => setIsRevealed(val => !val)}>
                {isRevealed ? "Hide" : "Show"}
            </button>
        )

        const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (inputProps.onChange) {
                if (parseValue) {
                    e.target.value = parseValue(e.target.value) || ""
                }
                inputProps.onChange(e)
            }
        }

        const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            e.target.value = e.target.value.trim()
            if (inputProps.onBlur) {
                if (parseValue) {
                    e.target.value = parseValue(e.target.value) || ""
                }
                inputProps.onBlur(e)
            }
        }

        const value = formatValue ? formatValue(propValue as Maybe<string>) : propValue
        const className = classNames(
            classes.base,
            size && classes.size[size],
            readOnly && classes.readonly,
            disabled && classes.disabled,
            propClassName
        )

        return mask ? (
            <InputMask
                maskChar={null}
                mask={mask}
                value={value || ""}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}>
                {maskInputProps => {
                    return (
                        <AntInput
                            autoComplete={autoComplete}
                            type={hideAbility && !isRevealed ? "password" : "text"}
                            className={className}
                            addonAfter={hideAbility ? renderHideBtn() : null}
                            ref={ref}
                            readOnly={readOnly}
                            disabled={disabled}
                            {...omit(["onBlur", "onChange", "value", "className", "onFocus"], inputProps)}
                            {...maskInputProps}
                        />
                    )
                }}
            </InputMask>
        ) : (
            <AntInput
                onChange={onChange}
                autoComplete={autoComplete}
                onBlur={onBlur}
                onFocus={onFocus}
                value={value || ""}
                type={hideAbility && !isRevealed ? "password" : "text"}
                className={className}
                addonAfter={hideAbility ? renderHideBtn() : null}
                ref={ref as LegacyRef<AntInput>}
                readOnly={readOnly}
                disabled={disabled}
                {...omit(["onBlur", "onChange", "value", "className", "onFocus"], inputProps)}
            />
        )
    }
)

Input.displayName = "Input"

const classes = {
    base: "!text-black/90",
    hideBtn: "w-[60px] cursor-pointer bg-transparent",
    size: {
        small: "h-[32px] text-13",
        default: "h-[34px] text-14",
        large: "h-[38px] text-15",
        "x-large": "h-[42px] text-16",
    },
    readonly: "border-black/10 focus:shadow-none bg-[#FAFAFA]",
    disabled: "border-black/10 focus:shadow-none bg-[#FAFAFA] !text-black/50 !opacity-100",
}
